import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg, downloadPdf } from "../../lib/api";
import { MODULES } from "../../config/modules";
import { PageHeader, StatusBadge } from "../../components/bits";
import { ArtifactForm } from "../../components/ArtifactTable";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Checkbox } from "../../components/ui/checkbox";
import { Plus, Pencil, Trash2, Link2, FileDown } from "lucide-react";

const STAGES = [
  { key: "ToBeAgreed", label: "To be agreed", color: "#A3A3A3" },
  { key: "Agreed", label: "Agreed", color: "#1E6FE8" },
  { key: "InBuild", label: "In build", color: "#5B93F0" },
  { key: "PVT", label: "PVT", color: "#FFB300" },
  { key: "UAT", label: "UAT", color: "#f97316" },
  { key: "SignedOff", label: "Signed off", color: "#00C781" },
];

export default function RequirementsPage() {
  const { projectId } = useParams();
  const config = MODULES.requirements;
  const [items, setItems] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkIds, setLinkIds] = useState([]);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/requirements`).then((r) =>
      setItems(r.data.sort((a, b) => String(a.reqId).localeCompare(String(b.reqId))))
    ).catch((e) => toast.error(errMsg(e)));
    api.get(`/projects/${projectId}/schedule`).then((r) => setTasks(r.data.tasks || [])).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const taskById = useMemo(() => Object.fromEntries(tasks.map((t) => [t.id, t])), [tasks]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(STAGES.map((s) => [s.key, 0]));
    items.forEach((r) => { if (c[r.status] !== undefined) c[r.status]++; });
    return c;
  }, [items]);
  const total = items.length || 1;
  const signedOff = counts.SignedOff || 0;

  const save = async () => {
    try {
      if (editing) await api.put(`/projects/${projectId}/artifacts/requirements/${editing.id}`, form);
      else await api.post(`/projects/${projectId}/artifacts/requirements`, form);
      setOpen(false); load(); toast.success("Requirement saved");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (item) => {
    try { await api.delete(`/projects/${projectId}/artifacts/requirements/${item.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const saveLinks = async () => {
    try {
      await api.put(`/projects/${projectId}/artifacts/requirements/${linkTarget.id}`, { ...linkTarget, linkedTaskIds: linkIds });
      setLinkTarget(null); load();
      toast.success(`${linkIds.length} tasks linked to ${linkTarget.reqId}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try { await downloadPdf(`/projects/${projectId}/export/rtm`, "RTM.pdf"); toast.success("RTM PDF downloaded"); }
    catch (e) { toast.error(errMsg(e)); }
    setExporting(false);
  };

  return (
    <div data-testid="artifact-page-requirements">
      <PageHeader overline={config.overline} title={config.title}
        actions={
          <>
            <Button data-testid="export-rtm-btn" variant="outline" className="rounded-none" onClick={exportPdf} disabled={exporting}>
              <FileDown className="h-4 w-4 mr-1" />{exporting ? "Exporting…" : "Export PDF"}
            </Button>
            <Button data-testid="add-requirements-btn" className="rounded-none" onClick={() => { setEditing(null); setForm({ status: "ToBeAgreed", priority: "H" }); setOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" /> Add Requirement
            </Button>
          </>
        }>
        <div className="text-xs text-muted-foreground mt-1">{items.length} requirements</div>
      </PageHeader>

      <div data-testid="rtm-progress-tracker" className="border border-border bg-card p-4 mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="overline-label">Sign-off progress</span>
          <span className="text-sm font-display font-black tracking-tight">
            {signedOff} / {items.length} signed off <span className="text-muted-foreground font-normal text-xs">({Math.round((signedOff / total) * 100)}%)</span>
          </span>
        </div>
        <div className="flex h-3 w-full overflow-hidden">
          {STAGES.map((s) => counts[s.key] > 0 && (
            <div key={s.key} data-testid={`rtm-progress-${s.key}`} style={{ width: `${(counts[s.key] / total) * 100}%`, background: s.color }}
              title={`${s.label}: ${counts[s.key]}`} />
          ))}
          {items.length === 0 && <div className="w-full bg-neutral-200" />}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
          {STAGES.map((s) => (
            <span key={s.key} className="inline-flex items-center gap-1.5 text-xs">
              <span className="h-2.5 w-2.5 inline-block" style={{ background: s.color }} />
              {s.label} <span className="font-semibold">{counts[s.key]}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card overflow-auto max-h-[calc(100vh-330px)]">
        <table className="w-full data-table text-sm" style={{ minWidth: 1200 }}>
          <thead>
            <tr>
              <th>Req ID</th><th style={{ minWidth: 240 }}>Requirement Description</th><th>Priority</th>
              <th>Agreed</th><th>Config / Build Ref</th><th>PVT</th><th>UAT</th><th>Sign-off</th><th>Status</th>
              <th style={{ minWidth: 180 }}>Linked Tasks</th><th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={11} className="text-center text-muted-foreground py-8">No requirements yet.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} data-testid="row-requirements">
                <td className="font-mono2 text-xs font-semibold whitespace-nowrap">{r.reqId}</td>
                <td>{r.description}</td>
                <td><StatusBadge value={r.priority} /></td>
                <td className="whitespace-nowrap text-xs">{r.agreedDate || "—"}</td>
                <td className="text-xs">{r.configBuildRef || "—"}</td>
                <td><StatusBadge value={r.pvt} /></td>
                <td><StatusBadge value={r.uat} /></td>
                <td className="whitespace-nowrap text-xs">{r.clientSignoffDate || "—"}</td>
                <td><StatusBadge value={r.status} /></td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    {(r.linkedTaskIds || []).map((tid) => {
                      const t = taskById[tid];
                      if (!t) return null;
                      return (
                        <span key={tid} data-testid="linked-task-chip" title={`${t.name} · ${t.status} · ${t.percentComplete || 0}%`}
                          className={`inline-block border px-1 py-0.5 text-[10px] font-mono2 rounded-sm ${
                            (t.percentComplete || 0) >= 100 ? "border-[#00C781] text-[#007a4f] bg-[#00C781]/10"
                            : t.computedCritical ? "border-[#FF3B30] text-[#b3221a] bg-[#FF3B30]/5"
                            : "border-neutral-400 text-neutral-600"}`}>
                          {t.wbsCode}
                        </span>
                      );
                    })}
                    {(r.linkedTaskIds || []).length === 0 && <span className="text-muted-foreground text-xs">—</span>}
                  </div>
                </td>
                <td className="whitespace-nowrap">
                  <button data-testid="link-tasks-btn" title="Link build tasks" onClick={() => { setLinkTarget(r); setLinkIds(r.linkedTaskIds || []); }}
                    className="p-1 hover:text-[#1E6FE8]"><Link2 className="h-3.5 w-3.5" /></button>
                  <button data-testid="edit-requirements-btn" onClick={() => { setEditing(r); setForm(r); setOpen(true); }} className="p-1 hover:text-[#1E6FE8]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid="delete-requirements-btn" onClick={() => remove(r)} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? `Edit ${editing.reqId}` : "Add Requirement"}</DialogTitle></DialogHeader>
          <ArtifactForm fields={config.fields} value={form} onChange={setForm} />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-requirements-btn" className="rounded-none" onClick={save}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!linkTarget} onOpenChange={(v) => !v && setLinkTarget(null)}>
        <DialogContent className="max-w-xl rounded-none max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-display">Link build tasks — {linkTarget?.reqId}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">{linkTarget?.description}</p>
          <div className="flex-1 overflow-y-auto border border-border">
            {tasks.map((t) => (
              <label key={t.id} className={`flex items-center gap-2 px-3 py-1.5 border-b border-border last:border-b-0 cursor-pointer hover:bg-muted ${!t.parentId ? "bg-neutral-100/70 font-semibold" : "pl-8"}`}>
                <Checkbox data-testid="link-task-checkbox" checked={linkIds.includes(t.id)}
                  onCheckedChange={(v) => setLinkIds(v ? [...linkIds, t.id] : linkIds.filter((x) => x !== t.id))} />
                <span className="font-mono2 text-xs w-12 shrink-0">{t.wbsCode}</span>
                <span className="text-sm flex-1">{t.name}</span>
                <StatusBadge value={t.status} />
              </label>
            ))}
          </div>
          <Button data-testid="save-links-btn" className="rounded-none" onClick={saveLinks}>
            Save {linkIds.length} linked task{linkIds.length === 1 ? "" : "s"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
